import { supabase } from '../supabaseClient';

export const visiteursService = {
  async getVisiteurs(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get visiteurs with conference info
      const { data: visiteurs } = await supabase
        .from('visiteurs')
        .select(`
          *,
          conferences!conference_id (titre)
        `)
        .eq('edition_id', edition.id)
        .order('created_at', { ascending: false });

      return (visiteurs || []).map(visiteur => ({
        id: visiteur.id,
        nom: visiteur.nom,
        prenom: visiteur.prenom,
        email: visiteur.email,
        telephone: visiteur.telephone,
        organisation: visiteur.organisation,
        paye: visiteur.paye,
        ville: visiteur.ville,
        categorie: visiteur.categorie,
        profil: visiteur.profil,
        informations: visiteur.informations,
        conference: visiteur.conference_id,
        conferenceTitre: visiteur.conferences?.titre || 'Conférence supprimée',
        dateInscription: visiteur.date_inscription,
        statut: visiteur.statut,
        referenceParticipation: visiteur.reference_participation,
        qrCodeData: visiteur.qr_code_data,
        qrCodeUrl: visiteur.qr_code_url,
        ticketSent: visiteur.ticket_sent,
        presenceMarkedAt: visiteur.presence_marked_at,
        typeParticipation: visiteur.type_participation,
        modeParticipation: visiteur.mode_participation,
        mot_cle: visiteur.mot_cle,
        axe: visiteur.axe,
        piece_jointe_url: visiteur.piece_jointe_url,
        photo_profil_url: visiteur.photo_profil_url,
        langue_preference: visiteur.langue_preference,
        jour_visiteur: visiteur.jour_visiteur || null
      }));
    } catch (error) {
      throw error;
    }
  },

  async addVisiteur(visiteurData, editionYear) {
    try {
      // Get edition ID first
      const { data: edition, error: editionError } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (editionError || !edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Validate and clean form data
      const cleanData = {
        nom: visiteurData.nom?.trim(),
        prenom: visiteurData.prenom?.trim(),
        email: visiteurData.email?.trim().toLowerCase(),
        telephone: visiteurData.telephone?.trim(),
        organisation: visiteurData.organisation?.trim() || null,
        paye: visiteurData.paye?.trim() || null,
        ville: visiteurData.ville?.trim() || null,
        profil: visiteurData.profil,
        categorie: visiteurData.categorie,
        informations: visiteurData.informations?.trim() || null,
        conference: visiteurData.conference ? parseInt(visiteurData.conference) : null,
        typeParticipation: visiteurData.typeParticipation || null,
        modeParticipation: visiteurData.modeParticipation || null,
        mot_cle: visiteurData.mot_cle?.trim() || null,
        axe: visiteurData.axe?.trim() || null,
        langue_preference: visiteurData.langue_preference || null,
        jour_visiteur: visiteurData.jour_visiteur?.trim() || null
      };

      // Validate required fields
      if (!cleanData.nom || !cleanData.prenom || !cleanData.email || !cleanData.telephone || !cleanData.paye || !cleanData.ville || !cleanData.profil) {
        throw new Error('Tous les champs requis de base doivent être remplis');
      }

      if (cleanData.typeParticipation === 'participant' && cleanData.categorie !== 'visiteur' && !cleanData.conference) {
        throw new Error('La conférence est requise pour les participants');
      }

      if (cleanData.typeParticipation === 'participant' && cleanData.categorie === 'visiteur' && !cleanData.jour_visiteur) {
        throw new Error('Veuillez sélectionner au moins un jour');
      }

      // Generate unique reference
      const reference = `GSIDA${Date.now()}`;

      // Check if email already exists for this edition using a different approach
      try {
        const { data: existingVisiteurs, error: checkError } = await supabase
          .from('visiteurs')
          .select('id, email')
          .eq('edition_id', edition.id)
          .eq('email', cleanData.email);

        if (checkError) {
        } else if (existingVisiteurs && existingVisiteurs.length > 0) {
          throw new Error('Cette adresse email est déjà enregistrée pour cette édition');
        }
      } catch (emailCheckError) {
      }

      // Prepare insert data with all required fields
      const insertData = {
        edition_id: edition.id,
        conference_id: cleanData.conference,
        nom: cleanData.nom,
        prenom: cleanData.prenom,
        email: cleanData.email,
        telephone: cleanData.telephone,
        organisation: cleanData.organisation,
        paye: cleanData.paye,
        ville: cleanData.ville,
        profil: cleanData.profil,
        categorie: cleanData.categorie || null,
        informations: cleanData.informations,
        type_participation: cleanData.typeParticipation,
        mode_participation: cleanData.modeParticipation,
        mot_cle: cleanData.mot_cle,
        axe: cleanData.axe,
        piece_jointe_url: visiteurData.piece_jointe_url || null,
        photo_profil_url: visiteurData.photo_profil_url || null,
        langue_preference: cleanData.langue_preference,
        jour_visiteur: cleanData.jour_visiteur,
        reference_participation: reference,
        statut: 'en_attente',
        date_inscription: new Date().toISOString().split('T')[0],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ticket_sent: false
      };

      // Use supabase client to insert directly (should work with anon policy)
      const { data, error } = await supabase
        .from('visiteurs')
        .insert([insertData])
        .select(`
          *,
          conferences!conference_id (titre)
        `)
        .single();

      if (error) {
        // Handle specific error cases
        if (error.code === '23505') {
          throw new Error('Cette adresse email est déjà enregistrée pour cette édition');
        } else if (error.code === '42501') {
          // RLS policy issue - provide helpful error
          throw new Error('Problème de configuration de sécurité. Veuillez contacter l\'administrateur.');
        } else if (error.code === '23503') {
          throw new Error('La conférence sélectionnée n\'existe pas ou a été supprimée');
        }

        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateVisiteur(visiteurId, visiteurData) {
    try {
      const updateData = {
        nom: visiteurData.nom,
        prenom: visiteurData.prenom,
        email: visiteurData.email,
        telephone: visiteurData.telephone,
        organisation: visiteurData.organisation || null,
        paye: visiteurData.paye || null,
        ville: visiteurData.ville || null,
        profil: visiteurData.profil,
        categorie: visiteurData.categorie,
        informations: visiteurData.informations || null,
        conference_id: visiteurData.conference ? parseInt(visiteurData.conference) : null,
        mode_participation: visiteurData.modeParticipation || null,
        langue_preference: visiteurData.langue_preference || null,
        jour_visiteur: visiteurData.jour_visiteur || null,
        date_inscription: visiteurData.dateInscription,
        statut: visiteurData.statut,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('visiteurs')
        .update(updateData)
        .eq('id', visiteurId)
        .select(`
          *,
          conferences!conference_id (titre)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateVisiteurStatus(visiteurId, nouveauStatut) {
    try {
      const updateData = {
        statut: nouveauStatut,
        updated_at: new Date().toISOString()
      };

      // If status is changing to 'valide', create reference if it doesn't exist
      if (nouveauStatut === 'valide') {
        const { data: visiteur } = await supabase
          .from('visiteurs')
          .select('reference_participation')
          .eq('id', visiteurId)
          .single();

        if (!visiteur.reference_participation) {
          updateData.reference_participation = `GSIDA${Date.now()}`;
        }
      }

      const { data, error } = await supabase
        .from('visiteurs')
        .update(updateData)
        .eq('id', visiteurId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteVisiteur(visiteurId) {
    try {
      const { error } = await supabase
        .from('visiteurs')
        .delete()
        .eq('id', visiteurId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async createParticipationReference(visiteurId) {
    try {
      const reference = `GSIDA${Date.now()}`;

      const { data, error } = await supabase
        .from('visiteurs')
        .update({
          reference_participation: reference,
          updated_at: new Date().toISOString()
        })
        .eq('id', visiteurId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async markTicketSent(visiteurId) {
    try {
      const { data, error } = await supabase
        .from('visiteurs')
        .update({
          ticket_sent: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', visiteurId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async markPresence(visiteurId) {
    try {
      const { data, error } = await supabase
        .from('visiteurs')
        .update({
          statut: 'present',
          presence_marked_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', visiteurId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async getVisiteurByReference(reference) {
    try {
      const { data, error } = await supabase
        .from('visiteurs')
        .select(`
          *,
          conferences!conference_id (titre, date_debut, heure_debut),
          editions!edition_id (year, name)
        `)
        .eq('reference_participation', reference)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async sendParticipationTicket(visiteurId) {
    try {
      // Get visiteur details
      const { data: visiteur } = await supabase
        .from('visiteurs')
        .select(`
          *,
          conferences!conference_id (titre, date_debut, heure_debut),
          editions!edition_id (year, name)
        `)
        .eq('id', visiteurId)
        .single();

      if (!visiteur) {
        throw new Error('Visiteur not found');
      }

      // Here you would integrate with an email service (like SendGrid, NodeMailer, etc.)
      // For now, we'll just simulate the email sending and mark the ticket as sent

      // Mark ticket as sent and update status to email_envoye
      await this.markTicketSent(visiteurId);
      await this.updateVisiteurStatus(visiteurId, 'email_envoye');

      return true;
    } catch (error) {
      return false;
    }
  },

  async sendWelcomeEmail(visiteurId) {
    try {
      // Get visiteur details
      const { data: visiteur } = await supabase
        .from('visiteurs')
        .select(`
          *,
          conferences!conference_id (titre, date_debut, heure_debut, salle)
        `)
        .eq('id', visiteurId)
        .single();

      if (!visiteur) return;

      // Conference info
      const conference = visiteur.conferences || {};
      const to_email = visiteur.email || '';
      const to_name = `${visiteur.prenom || ''} ${visiteur.nom || ''}`.trim();
      const conference_titre = conference.titre || '';
      const conference_date = conference.date_debut || '';
      const conference_salle = conference.salle || '';

      // Use EmailJS (must be available globally or imported)
      if (window.emailjs) {
        await window.emailjs.send(
          'service_dyae2ak',
          'template_2tcwp6f',
          {
            to_email,
            to_name,
            conference_titre,
            conference_date,
            conference_salle,
          },
          '_UeBfJMtIUI_gP-mW'
        );

        // Update status to email_envoye after successful email send
        await this.updateVisiteurStatus(visiteurId, 'email_envoye');
      }
    } catch (error) {
    }
  },

  async uploadPieceJointe(file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `pieces_jointes/${fileName}`;

      const { data, error } = await supabase.storage
        .from('visiteurs_files')
        .upload(filePath, file);

      if (error) {
        throw new Error('Erreur lors du téléchargement de la pièce jointe (assurez-vous que le bucket "visiteurs_files" existe).');
      }

      const { data: urlData } = supabase.storage
        .from('visiteurs_files')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      throw error;
    }
  },

  async uploadPhotoProfil(file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `photos_profil/${fileName}`;

      const { data, error } = await supabase.storage
        .from('visiteurs_files')
        .upload(filePath, file);

      if (error) {
        throw new Error('Erreur lors du téléchargement de la photo (assurez-vous que le bucket "visiteurs_files" existe).');
      }

      const { data: urlData } = supabase.storage
        .from('visiteurs_files')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      throw error;
    }
  }
};